import React, { useEffect, useMemo, useRef, useState } from "react";
import FormField from "../../../dynamicFormBuilder/FormField";
import { TOTALS_FIELD, TOTALS_LAYOUT_ID, TOTALS_LAYOUT_NAME } from "../constants";
import { TotalsLayout } from "./TotalsLayout";

/** Sums a field over the selected rows, treating null and undefined as zero. */
const sum = (rows, field) =>
    rows.reduce((total, row) => total + (Number(row?.[field]) || 0), 0);

/**
 * The bottom <b>Totals</b> panel.
 *
 * The only bottom tab that is a form rather than a grid, so it is built with the project's dynamic
 * form (`FormField` + a layout schema) instead of `BottomGrid`.
 *
 * <b>What it totals.</b> The rows selected in the active top grid - not the whole grid, and not the
 * focused row alone. That is the Delphi's behaviour: <c>ChangeNode</c> seeds the six values from the
 * current row, and <c>SelectedCountChange</c> adds and subtracts them as the selection grows and
 * shrinks (uFMD0000.pas:12986 onwards).
 *
 * The web sums the current selection outright rather than maintaining a running total. Same answer,
 * and it cannot drift: the Delphi's incremental arithmetic depends on every selection event arriving
 * in order, which is why that code carries a <c>dPrev*</c> shadow copy of all six values.
 *
 * All six come from the top grid's own rows, so the panel needs no request of its own.
 */
const TotalsTab = (props) => {
    const { selectedRows, currentUser, dynamicFormSetting } = props;

    const rows = useMemo(() => selectedRows ?? [], [selectedRows]);

    const totals = useMemo(
        () => ({
            [TOTALS_FIELD.Items]: sum(rows, "ItemsOrdered"),
            [TOTALS_FIELD.Weight]: sum(rows, "WeightOrdered"),
            [TOTALS_FIELD.Cubes]: sum(rows, "CubesOrdered"),
            [TOTALS_FIELD.Pallets]: sum(rows, "PalletsOrdered"),
            [TOTALS_FIELD.FreightRevenue]: sum(rows, "OmTotalFreight"),
            [TOTALS_FIELD.MerchandiseRevenue]: sum(rows, "OmTotalMerchandise"),
        }),
        [rows]
    );

    // FormField owns the field values through formFields/setformFields, so the computed totals are
    // pushed into that state rather than rendered directly.
    const [formFields, setFormFields] = useState(totals);
    const formRefs = useRef({});

    useEffect(() => {
        setFormFields(totals);
    }, [totals]);

    return (
        <div className="dm-totals">
            <FormField
                currrentUser={currentUser}
                layoutId={TOTALS_LAYOUT_ID}
                layoutName={TOTALS_LAYOUT_NAME}
                defaultFormSchema={TotalsLayout}
                formFields={formFields}
                setformFields={setFormFields}
                // Read-only panel: there is no field-change or form-load behaviour to run, but
                // FormField calls both unconditionally, so they are supplied as no-ops.
                handleFieldValueChange={() => {}}
                onFormLoad={() => {}}
                DYNAMICFORMSETTING={dynamicFormSetting}
                formRefs={formRefs}
            />
        </div>
    );
};

export default TotalsTab;
